import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ChevronRight, ArrowLeft, Link2, Edit, User, Newspaper } from 'lucide-react'; 
import { newsService } from '../../services/contentService';
import catalogService from '../../services/catalogService'; 
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl, cleanHtmlContent, generateSlug, fixHtmlImageUrls } from '../../utils/helpers';
import { toast } from 'react-toastify';

import 'react-quill-new/dist/quill.snow.css';

// ─────────────────────────────────────────────────────────────
// TỐI ƯU HTML INJECTION (FIX CLS & LCP TỪ CORE HTML)
// ─────────────────────────────────────────────────────────────
const SafeHtml = React.memo(React.forwardRef(({ content, className }, ref) => {
    if (!content) return null;
    
    // 1. Dọn dẹp khoảng trắng/ký tự thừa
    const cleaned = cleanHtmlContent(content);
    // 2. Chèn domain Backend vào tất cả ảnh bị thiếu
    let finalHtml = fixHtmlImageUrls(cleaned);

    // 3. TỐI ƯU LCP & CLS BẰNG REGEX (RẤT QUAN TRỌNG CHO TIN TỨC)
    let firstImgFound = false;
    finalHtml = finalHtml.replace(/<img\s[^>]*>/gi, (match) => {
        if (!firstImgFound) {
            firstImgFound = true;
            // Ảnh đầu tiên: LCP Element -> Ép tải nhanh nhất, cấm lazy
            let newMatch = match;
            if (!newMatch.match(/fetchpriority/i)) {
                newMatch = newMatch.replace(/<img\s/i, '<img fetchpriority="high" loading="eager" ');
            }
            return newMatch;
        } else {
            // Các ảnh sau: Ép lazy load để tiết kiệm băng thông và tăng tốc độ parse
            let newMatch = match;
            if (!newMatch.match(/loading/i)) {
                newMatch = newMatch.replace(/<img\s/i, '<img loading="lazy" decoding="async" ');
            }
            return newMatch;
        }
    });

    return (
        <div
            ref={ref}
            className={className}
            dangerouslySetInnerHTML={{ __html: finalHtml }}
        />
    );
}));

const NewsDetailPage = () => {
    const { slug } = useParams();
    
    // TỐI ƯU: Memoize việc bóc tách ID
    const id = useMemo(() => slug ? slug.split('-id').pop() : null, [slug]);
    
    const [news, setNews] = useState(null);
    const [relatedNews, setRelatedNews] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [loadingRelated, setLoadingRelated] = useState(true); 
    const [isAdminOrStaff, setIsAdminOrStaff] = useState(false); 

    // Kiểm tra quyền
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const rolesObj = payload.scope || payload.roles || payload.authorities || payload.role || '';
                const rolesString = typeof rolesObj === 'string' ? rolesObj : JSON.stringify(rolesObj);

                if (rolesString.includes('ADMIN') || rolesString.includes('STAFF') || rolesString.includes('ROLE_ADMIN') || rolesString.includes('ROLE_STAFF')) {
                    setIsAdminOrStaff(true);
                }
            } catch (error) { console.error("Lỗi khi giải mã token:", error); }
        }
    }, []);

    useEffect(() => {
        if (!id) return;

        // Cuộn lên đầu trang mượt mà nhưng ngay lập tức để tránh layout shift từ trang trước
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        let isMounted = true;

        const fetchMainArticle = async () => {
            try {
                setLoading(true);
                setLoadingRelated(true);
                
                const res = await newsService.getNewsDetail(id);
                const currentNews = res?.data || res;
                
                if (isMounted) {
                    setNews(currentNews);
                    setLoading(false); // Render bài viết ngay lập tức
                }

                // Gọi ngầm tin liên quan sau khi đã có data bài viết
                fetchRelatedData(currentNews);

            } catch (error) {
                console.error("Lỗi lấy chi tiết tin tức:", error);
                if (isMounted) {
                    setLoading(false);
                    setLoadingRelated(false);
                }
            }
        };

        const fetchRelatedData = async (currentNews) => {
            if (!currentNews) return;
            try {
                const [allNewsRes, configRes] = await Promise.all([
                    newsService.getPublicNews().catch(() => []),
                    catalogService.getSystemConfig().catch(() => ({}))
                ]);

                if (!isMounted) return;

                const allNews = Array.isArray(allNewsRes?.data) ? allNewsRes.data : (Array.isArray(allNewsRes) ? allNewsRes : []);
                const conf = configRes?.data || configRes || {};
                
                const displayLimit = parseInt(conf.related_news_limit) || 5;
                
                const filteredRelated = allNews
                    .filter(n => n.newsId !== currentNews.newsId && (!currentNews.categoryId || n.categoryId === currentNews.categoryId))
                    .sort((a, b) => new Date(b.newsCreatedTime || b.createdAt || 0) - new Date(a.newsCreatedTime || a.createdAt || 0))
                    .slice(0, displayLimit);
                
                setRelatedNews(filteredRelated);
                setLoadingRelated(false);

            } catch (error) {
                console.error("Lỗi lấy dữ liệu liên quan:", error);
                if (isMounted) setLoadingRelated(false);
            }
        };

        fetchMainArticle();

        return () => {
            isMounted = false;
        };
    }, [id]); 

    useEffect(() => {
        if (news?.newsTitle) {
            document.title = `${news.newsTitle} | Thành Nam PC`;
            let metaDescriptionTag = document.querySelector('meta[name="description"]');
            if (metaDescriptionTag) {
                const dynamicDescription = news.newsExcerpt 
                    ? news.newsExcerpt 
                    : `Đọc bài viết: ${news.newsTitle} tại Thành Nam PC. Cập nhật tin tức công nghệ, thủ thuật và khuyến mãi mới nhất.`;
                
                const seoOptimizedDesc = dynamicDescription.length > 155 
                    ? dynamicDescription.substring(0, 152) + '...' 
                    : dynamicDescription;

                metaDescriptionTag.setAttribute("content", seoOptimizedDesc);
            }
        }
    }, [news]);

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({ title: news?.newsTitle, url: window.location.href }).catch(console.error);
        } else {
            navigator.clipboard.writeText(window.location.href);
            toast.success("Đã copy đường dẫn bài viết!");
        }
    };

    if (!loading && !news) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <Header />
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-20 min-h-[60vh]">
                    <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-6"><span className="text-4xl text-gray-400">📄</span></div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Không tìm thấy bài viết!</h2>
                    <Link to="/tin-tuc" className="bg-tnc-orange text-white font-bold px-6 py-3 rounded-lg hover:bg-tnc-orange-hover transition-colors flex items-center gap-2 shadow-sm">
                        <ArrowLeft size={18}/> Về trang Tin tức
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col font-inter">
            <Header />
            
            {/* Thanh Breadcrumb - Đảm bảo min-h để chống giật */}
            <div className="bg-gradient-to-r from-orange-100 via-orange-50 to-white border-b border-orange-200/60 sticky top-0 z-30 shadow-sm min-h-[48px] flex items-center">
                <div className="container mx-auto px-4 py-3.5 text-[13px] md:text-sm flex items-center gap-1.5 md:gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                    <Link to="/" className="text-gray-600 font-bold hover:text-tnc-orange transition-colors">Trang chủ</Link>
                    <ChevronRight size={14} className="flex-shrink-0 text-gray-400" />
                    <Link to="/tin-tuc" className="text-gray-600 font-bold hover:text-tnc-orange transition-colors">Tin tức</Link>
                    <ChevronRight size={14} className="flex-shrink-0 text-gray-400" />
                    {loading ? (
                        <div className="w-32 md:w-64 h-4 bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                        <span className="text-gray-900 font-extrabold truncate" title={news.newsTitle}>{news.newsTitle}</span>
                    )}
                </div>
            </div>

            <main className="flex-grow py-4 md:py-6">
                <div className="container mx-auto px-4 max-w-7xl">
                    {loading ? (
                        <article className="rounded-2xl shadow-sm border border-orange-100 overflow-hidden bg-white animate-pulse">
                            <div className="px-5 pb-5 pt-3 md:px-8 md:pb-8 md:pt-5 lg:px-10 lg:pb-10 lg:pt-6">
                                {/* Thẻ tag skeleton */}
                                <div className="w-20 h-6 bg-orange-100 rounded-full mb-3"></div>
                                {/* Tiêu đề skeleton (min-h tương đương h1 thực tế) */}
                                <div className="w-full md:w-3/4 h-8 md:h-10 bg-gray-200 rounded mb-4"></div>
                                {/* Ngày tháng & chia sẻ skeleton */}
                                <div className="flex justify-between items-center mb-6">
                                    <div className="w-32 h-6 bg-gray-200 rounded-full"></div>
                                    <div className="w-24 h-6 bg-gray-200 rounded-full hidden sm:block"></div>
                                </div>
                                {/* Nội dung giả */}
                                <div className="w-full h-4 bg-gray-200 rounded mb-3 mt-4"></div>
                                <div className="w-11/12 h-4 bg-gray-200 rounded mb-3"></div>
                                <div className="w-4/5 h-4 bg-gray-200 rounded mb-8"></div>
                                {/* Khu vực ảnh (LCP Skeleton - Rất quan trọng để chặn CLS) */}
                                <div className="w-full aspect-[16/9] md:aspect-[21/9] max-h-[500px] bg-gray-200 rounded-xl mb-6"></div>
                                <div className="w-full h-4 bg-gray-200 rounded mb-3"></div>
                                <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
                            </div>
                        </article>
                    ) : (
                        <article className="rounded-2xl shadow-sm border border-orange-200/60 overflow-hidden bg-gradient-to-r from-orange-50 to-white">
                            <div className="px-5 pb-5 pt-3 md:px-8 md:pb-8 md:pt-5 lg:px-10 lg:pb-10 lg:pt-6 text-left">
                                <div className="inline-block bg-orange-50 text-tnc-orange text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-3 border border-orange-100">
                                    {news.categoryName || 'Tin tức'}
                                </div>
                                
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    {/* FIX CLS: Đảm bảo Title có min-height để không kéo layout giật lên xuống */}
                                    <h1 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-[#002855] leading-snug min-h-[32px] md:min-h-[40px]">{news.newsTitle}</h1>
                                    {isAdminOrStaff && (
                                        <Link 
                                            to={`/admin/news/edit/${news.newsId}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-colors shadow-sm flex-shrink-0" 
                                            title="Chỉnh sửa bài viết trong tab mới"
                                        >
                                            <Edit size={20} />
                                        </Link>
                                    )}
                                </div>
                                
                                <div className="flex flex-wrap items-center justify-between gap-4 pb-2">
                                    <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full text-sm text-gray-500 font-medium shadow-sm">
                                        <Calendar size={15} className="text-tnc-orange"/>
                                        {new Date(news.newsCreatedTime || news.createdAt).toLocaleDateString('vi-VN')}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-bold text-gray-600 hidden sm:block">Chia sẻ:</span>
                                        <div className="flex gap-2">
                                            <button onClick={handleShare} className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm"><svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></button>
                                            <button onClick={handleShare} className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center hover:bg-sky-600 transition-colors shadow-sm"><svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg></button>
                                            <button onClick={handleShare} className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 transition-colors shadow-sm"><Link2 size={14} /></button>
                                        </div>
                                    </div>
                                </div>

                                {news.newsExcerpt && (
                                    <div className="pt-3 pb-2">
                                        <p className="text-base font-bold text-gray-800 leading-relaxed border-l-4 border-tnc-orange pl-5 py-1">
                                            {news.newsExcerpt}
                                        </p>
                                    </div>
                                )}

                                <div className="ql-snow pt-4">
                                    <SafeHtml 
                                        content={news.newsContent || ''}
                                        className="ql-editor !p-0 html-content text-gray-800 text-[15px] md:text-[16px] leading-relaxed 
                                            [&_*]:!bg-transparent
                                            [&_p]:min-h-[1.5em] [&_p]:mb-4 
                                            [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-xl [&_img]:mx-auto [&_img]:my-4 [&_img]:min-h-[250px] md:[&_img]:min-h-[400px] [&_img]:bg-gray-50 [&_img]:object-contain
                                            [&_a]:break-all [&_a]:text-tnc-orange [&_a]:underline
                                            [&_table]:w-full [&_table]:border-collapse [&_table]:my-6 [&_table]:block [&_table]:overflow-x-auto
                                            [&_th]:border [&_th]:border-orange-200/60 [&_th]:bg-orange-100/50 [&_th]:p-3 [&_th]:text-left
                                            [&_td]:border [&_td]:border-orange-200/60 [&_td]:p-3
                                            [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:my-4 [&_h1]:text-[#002855]
                                            [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:my-3 [&_h2]:text-[#002855]
                                            [&_h3]:text-xl [&_h3]:font-bold [&_h3]:my-2 [&_h3]:text-[#002855]
                                            [&_strong]:font-bold [&_em]:italic"
                                    />
                                </div>
                            </div>
                            
                            <div className="px-5 md:px-8 lg:px-10 py-5 border-t border-orange-200/60 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-orange-50 to-white">
                                <div className="text-sm font-medium text-gray-500">
                                    Nguồn: <span className="font-bold text-gray-800">Thành Nam PC</span>
                                </div>
                                
                                {news.authorName && (
                                    <div className="flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-bold text-[#002855] shadow-sm">
                                        <User size={16} className="text-tnc-orange" /> Người đăng: {news.authorName}
                                    </div>
                                )}
                            </div>
                        </article>
                    )}
                </div>

                {/* Khu vực Tin Liên Quan */}
                {/* FIX LCP: Thêm [content-visibility:auto] để trình duyệt delay render nếu khu vực này nằm ngoài màn hình */}
                <div className="container mx-auto px-4 max-w-7xl mt-12 min-h-[300px] [content-visibility:auto]">
                    <section>
                        <h3 className="text-2xl font-extrabold text-[#002855] mb-6 flex items-center gap-2 border-b border-orange-200/60 pb-3">
                            <Newspaper className="text-tnc-orange" size={28}/> Tin tức cùng chuyên mục
                        </h3>
                        
                        {loadingRelated ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 animate-pulse flex flex-col min-h-[220px]">
                                        <div className="aspect-video w-full bg-gray-200"></div>
                                        <div className="p-3 md:p-4 flex flex-col gap-2">
                                            <div className="w-1/2 h-3 bg-gray-200 rounded"></div>
                                            <div className="w-full h-4 bg-gray-200 rounded mt-1"></div>
                                            <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : relatedNews.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                                {relatedNews.map(item => (
                                    <Link to={`/tin-tuc/${generateSlug(item.newsTitle)}-id${item.newsId}`} key={item.newsId} className="bg-white rounded-xl overflow-hidden shadow-sm border border-orange-100 hover:shadow-md hover:border-tnc-orange hover:-translate-y-1 transition-all duration-300 flex flex-col min-h-[220px]">
                                        <div className="aspect-video w-full bg-gray-100 overflow-hidden relative">
                                            <img src={getImageUrl(item.newsImageUrl || item.newsImage)} alt={item.newsTitle} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                            {item.categoryName && (
                                                <div className="absolute top-2 left-2 bg-[#002855] text-white text-[10px] font-extrabold px-2.5 py-1 rounded shadow-sm uppercase tracking-wide">
                                                    {item.categoryName}
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3 md:p-4 flex flex-col flex-1">
                                            <div className="text-[10px] md:text-[11px] text-gray-500 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <Calendar size={12}/> {new Date(item.newsCreatedTime || item.createdAt).toLocaleDateString('vi-VN')}
                                            </div>
                                            <h4 className="font-bold text-gray-800 text-sm md:text-[15px] line-clamp-2 hover:text-tnc-orange transition-colors leading-snug">
                                                {item.newsTitle}
                                            </h4>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic text-sm">Chưa có bài viết liên quan.</div>
                        )}
                    </section>
                </div>

                <div className="mt-10 text-center">
                    <Link 
                        to="/tin-tuc" 
                        state={{ categoryId: news?.categoryId || 'all' }} 
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-tnc-orange font-bold transition-colors bg-white px-6 py-3 rounded-full shadow-sm border border-orange-100"
                    >
                        <ArrowLeft size={20} /> QUAY LẠI DANH SÁCH TIN TỨC
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default NewsDetailPage;